import React, { createContext, useContext, useMemo } from 'react';
import { convertLocalDateToTimezonedTimestamp, convertLocalToUTC, convertTimezonedTimestampToLocalDate, convertToLocalDate, formatDateInTimezone, formatUTCWithTimezoneOffset, parseUTCToLocal, } from '@fourthwall/utils/dist/timezone';
import { useGetShopTimezone } from '../hooks/useGetShopTimezone';
const defaultTimezone = 'UTC';
const defaultTimezoneAbbreviation = 'CET';
const ShopTimeContext = createContext({
    timezone: defaultTimezone,
    timezoneAbbreviation: defaultTimezoneAbbreviation,
    getCurrentShopTime: () => parseUTCToLocal(new Date(), defaultTimezone),
    getCurrentRelativeShopTime: () => new Date(),
    toShopTime: (date) => parseUTCToLocal(date, defaultTimezone),
    toUTC: (date) => convertLocalToUTC(date, defaultTimezone),
    formatInShopTime: (date, formatStr) => formatDateInTimezone(date, defaultTimezone, formatStr),
    toUTCWithOffset: (date, formatStr) => formatUTCWithTimezoneOffset(date, defaultTimezone, formatStr),
    convertDateToTimestamp: (date) => convertLocalDateToTimezonedTimestamp(date, defaultTimezone),
    convertTimestampToDate: (timestamp) => convertTimezonedTimestampToLocalDate(timestamp, defaultTimezone),
    convertToDate: (date) => convertToLocalDate(date, defaultTimezone),
});
export const ShopTimeProvider = ({ children }) => {
    const shopTimezone = useGetShopTimezone();
    const timezone = shopTimezone.name;
    const toShopTime = useMemo(() => (date) => parseUTCToLocal(date, timezone), [timezone]);
    const toUTC = useMemo(() => (date) => convertLocalToUTC(date, timezone), [timezone]);
    const formatInShopTime = useMemo(() => (date, formatStr = 'YYYY-MM-DD HH:mm') => formatDateInTimezone(date, timezone, formatStr), [timezone]);
    const toUTCWithOffset = useMemo(() => (date, formatStr = 'YYYY-MM-DDTHH:mm:ssZ') => formatUTCWithTimezoneOffset(date, timezone, formatStr), [timezone]);
    const getCurrentShopTime = useMemo(() => {
        return () => toShopTime(new Date());
    }, [toShopTime]);
    const getCurrentRelativeShopTime = useMemo(() => {
        return () => new Date(getCurrentShopTime().format('YYYY-MM-DDTHH:mm:ss'));
    }, [getCurrentShopTime]);
    const convertDateToTimestamp = useMemo(() => (date) => convertLocalDateToTimezonedTimestamp(date, timezone), [timezone]);
    const convertTimestampToDate = useMemo(() => (timestamp) => convertTimezonedTimestampToLocalDate(timestamp, timezone), [timezone]);
    const convertToDate = useMemo(() => (date) => convertToLocalDate(date, timezone), [timezone]);
    const value = useMemo(() => ({
        timezone,
        timezoneAbbreviation: shopTimezone.abbreviation,
        getCurrentShopTime,
        getCurrentRelativeShopTime,
        toShopTime,
        toUTC,
        formatInShopTime,
        toUTCWithOffset,
        convertDateToTimestamp,
        convertTimestampToDate,
        convertToDate,
    }), [
        formatInShopTime,
        shopTimezone.abbreviation,
        timezone,
        getCurrentShopTime,
        getCurrentRelativeShopTime,
        toShopTime,
        toUTC,
        toUTCWithOffset,
        convertDateToTimestamp,
        convertTimestampToDate,
        convertToDate,
    ]);
    return React.createElement(ShopTimeContext.Provider, { value: value }, children);
};
export const useShopTime = () => useContext(ShopTimeContext);
